import Container from "Components/Container/Container";
import React, { useState, useCallback, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import IMAGE_URL from "Utilities/IMAGE_URL";

const Slider = ({
  title,
  subTitle,
  data,
  type = "course",
  slideNumber = 3,
  isGrey = false,
}) => {
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Helper function to determine the link path based on item type
  const getItemLink = (item) => {
    switch (type) {
      case "course":
        return `/course-detailes/${item.slug}`;
      case "product":
        return `/product/${item.slug}`;
      case "book":
        return `/book/${item.slug}`;
      default:
        return `/course-detailes/${item.slug}`;
    }
  };

  // Update navigation state
  const updateNavState = useCallback((swiper) => {
    if (swiper) {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    }
  }, []);

  // Handle previous slide
  const handlePrev = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (swiperInstance && !swiperInstance.isBeginning) {
        swiperInstance.slidePrev();
        // Update state after animation
        setTimeout(() => updateNavState(swiperInstance), 50);
      }
    },
    [swiperInstance, updateNavState]
  );

  // Handle next slide
  const handleNext = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (swiperInstance && !swiperInstance.isEnd) {
        swiperInstance.slideNext();
        // Update state after animation
        setTimeout(() => updateNavState(swiperInstance), 50);
      }
    },
    [swiperInstance, updateNavState]
  );

  // Initialize swiper
  const onSwiperInit = useCallback(
    (swiper) => {
      setSwiperInstance(swiper);
      updateNavState(swiper);
      // Small delay to ensure everything is ready
      setTimeout(() => {
        setIsReady(true);
        updateNavState(swiper);
      }, 100);
    },
    [updateNavState]
  );

  // Handle slide change
  const onSlideChange = useCallback(
    (swiper) => {
      updateNavState(swiper);
    },
    [updateNavState]
  );

  // Also listen for resize events
  useEffect(() => {
    if (swiperInstance) {
      const handleResize = () => {
        updateNavState(swiperInstance);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [swiperInstance, updateNavState]);

  // If no data or empty array, don't render the slider
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <section
      className={`${
        isGrey
          ? "bg-lightGray/20 py-secondary mt-secondary"
          : "bg-white mt-secondary lg:mt-primary"
      } `}
    >
      <Container>
        <div className="flex items-center justify-center lg:justify-between flex-col mb-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-center font-title">
            {title}
          </h2>
          {subTitle && (
            <p className="text-sm text-gray-500 mt-2 md:mt-0">{subTitle}</p>
          )}
        </div>

        <div className="relative px-12 md:px-14">
          {/* Previous Button - Positioned outside swiper container */}
          <button
            type="button"
            onClick={handlePrev}
            disabled={!isReady || isBeginning}
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 50,
              pointerEvents: "auto",
            }}
            className={`flex items-center justify-center bg-white text-xl text-primary border border-primary rounded-full p-2 shadow-md transition-all duration-200 select-none ${
              !isReady || isBeginning
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-primary hover:text-white cursor-pointer active:scale-95"
            }`}
            aria-label="Previous slide"
          >
            <CaretLeft size={20} weight="bold" />
          </button>

          {/* Swiper Container */}
          <div className="overflow-hidden">
            <Swiper
              onSwiper={onSwiperInit}
              onSlideChange={onSlideChange}
              onResize={onSlideChange}
              spaceBetween={10}
              slidesPerView={1}
              watchOverflow={true}
              observer={true}
              observeParents={true}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: slideNumber },
              }}
              className="mySwiper"
            >
              {data.map((item, index) => (
                <SwiperSlide key={item._id || item.slug || index}>
                  <Link to={getItemLink(item)} className="block">
                    <div className="overflow-hidden rounded-lg">
                      <div className="h-[300px] w-full relative overflow-hidden">
                        <img
                          src={
                            item.image && item.image.data
                              ? `${IMAGE_URL}/${item.image.data}`
                              : `${IMAGE_URL}/${item.image}`
                          }
                          alt={item.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                      <div className="mt-2 text-center">
                        {item.subCategory && (
                          <p className="text-primary font-medium mb-2">
                            {item.subCategory.title}
                          </p>
                        )}
                        {item.category && (
                          <p className="text-primary font-medium mb-2">
                            {typeof item.category === "object"
                              ? item.category.title ||
                                item.category.translations?.en?.title
                              : item.category}
                          </p>
                        )}
                        <h3 className="font-medium text-xl capitalize truncate">
                          {item.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Next Button - Positioned outside swiper container */}
          <button
            type="button"
            onClick={handleNext}
            disabled={!isReady || isEnd}
            style={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 50,
              pointerEvents: "auto",
            }}
            className={`flex items-center justify-center bg-white text-xl text-primary border border-primary rounded-full p-2 shadow-md transition-all duration-200 select-none ${
              !isReady || isEnd
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-primary hover:text-white cursor-pointer active:scale-95"
            }`}
            aria-label="Next slide"
          >
            <CaretRight size={20} weight="bold" />
          </button>
        </div>
      </Container>
    </section>
  );
};

export default Slider;
